/**
 * ═══════════════════════════════════════════════════════════════════════
 * AuthContext — Unified Auth State Management
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Stores:
 *   userType        — "INDIVIDUAL" | "ORGANISATION"
 *   loginMethod     — "EMAIL" | "PHONE"
 *   username        — email or phone that was used
 *   session         — Cognito session token for verify-otp challenge
 *   accessToken     — JWT (also sent via httpOnly cookie)
 *   refreshToken    — JWT
 *   isAuthenticated — boolean
 *
 * All auth pages read / write through this context so state survives
 * cross-page navigation (signup → verify-otp → set-password).
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { STORAGE_KEYS } from '../constants';

// ── Initial state ────────────────────────────────────────────────────

const initialState = {
  userType: 'ORGANISATION',
  loginMethod: 'USERNAME',
  username: '',
  session: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
};

// ── Action types ─────────────────────────────────────────────────────

const ActionTypes = {
  SET_USER_TYPE: 'SET_USER_TYPE',
  SET_LOGIN_METHOD: 'SET_LOGIN_METHOD',
  SET_USERNAME: 'SET_USERNAME',
  SET_SESSION: 'SET_SESSION',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  RESET_FLOW: 'RESET_FLOW',
};

// ── Reducer ──────────────────────────────────────────────────────────

function authReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_USER_TYPE:
      return { ...state, userType: action.payload };
    case ActionTypes.SET_LOGIN_METHOD:
      return { ...state, loginMethod: action.payload };
    case ActionTypes.SET_USERNAME:
      return { ...state, username: action.payload };
    case ActionTypes.SET_SESSION:
      return { ...state, session: action.payload };
    case ActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        accessToken: action.payload.accessToken || null,
        refreshToken: action.payload.refreshToken || null,
      };
    case ActionTypes.LOGOUT:
      return { ...initialState };
    case ActionTypes.RESET_FLOW:
      return {
        ...state,
        username: '',
        session: null,
      };
    default:
      return state;
  }
}

// ── Context ──────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const setUserType = useCallback((ut) => {
    dispatch({ type: ActionTypes.SET_USER_TYPE, payload: ut });
  }, []);

  const setLoginMethod = useCallback((lm) => {
    dispatch({ type: ActionTypes.SET_LOGIN_METHOD, payload: lm });
  }, []);

  const setUsername = useCallback((u) => {
    dispatch({ type: ActionTypes.SET_USERNAME, payload: u });
  }, []);

  const setSession = useCallback((s) => {
    dispatch({ type: ActionTypes.SET_SESSION, payload: s });
  }, []);

  const loginSuccess = useCallback((tokens) => {
    dispatch({ type: ActionTypes.LOGIN_SUCCESS, payload: tokens });
  }, []);

  const logout = useCallback(() => {
    // Clear stored session
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    dispatch({ type: ActionTypes.LOGOUT });
  }, []);

  const resetFlow = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_FLOW });
  }, []);

  const value = {
    ...state,
    setUserType,
    setLoginMethod,
    setUsername,
    setSession,
    loginSuccess,
    logout,
    resetFlow,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
